import { ExploreByDayCarouselWrapperProps } from '@msc-b2c/interfaces/types';
import ExploreByDayCarousel from './ExploreByDayCarousel';
import { useSelector } from 'react-redux';
import { getActiveSession, RootState, useGetBookingQuery } from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import styles from './explorebydaycarousel.module.scss';
import { BookingStatusEnum, LoginMode } from '@msc-b2c/interfaces/enums';
import { getDepartureDate, isXDaysBeforeDeparture } from '../utils/myAreaUtils';
import { Text } from '@msc-b2c/shared-components';

export const ExploreByDayCarouselWrapper = ({
  preTitle,
  title,
  subTitle,
  dayAtSeaLabel,
  dayPlaceHolder,
  dayCardDateFormat,
  dayAtSeaInfoLabel,
  arrivalOnlyInfoLabel,
  departureOnlyInfoLabel,
  touchingPortInfoLabel,
  ctaLinkLabel,
  ctaLinkIcon,
  showSliderMobile,
  showSliderDesktop,
  scrollLeftIcon,
  scrollRightIcon,
  oceanKayRibbonLabel,
  oceanKayRibbonBgColor,
  oceanKayRibbonTextColor,
  rediractionLinkUrl,
  dataComponentName,
}: ExploreByDayCarouselWrapperProps) => {
  const configurationProps: ExploreByDayCarouselWrapperProps = {
    preTitle,
    title,
    subTitle,
    ctaLinkLabel,
    ctaLinkIcon,
    showSliderMobile,
    showSliderDesktop,
    scrollLeftIcon,
    scrollRightIcon,
    oceanKayRibbonLabel,
    oceanKayRibbonBgColor,
    oceanKayRibbonTextColor,
    rediractionLinkUrl,
    dayAtSeaLabel,
    dayPlaceHolder,
    dayCardDateFormat,
    dayAtSeaInfoLabel,
    arrivalOnlyInfoLabel,
    departureOnlyInfoLabel,
    touchingPortInfoLabel,
    dataComponentName,
  };

  const itineraryData = useSelector((state: RootState) => state.myCruise.itinerary);
  const excursionData = useSelector((state: RootState) => state.myCruise.excursions?.excursions);

  const { loginMode, bookingNumber = '' } = getActiveSession();

  // GET BOOKING API CALL
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);

  const bookingStatusCode: string | undefined = booking?.status.code;
  const isOpt: boolean = bookingStatusCode === BookingStatusEnum.OPTIONAL;

  function removeExploreByDaySkeleton() {
    const skeletonDiv = document.querySelector('.cmp-explorebydaycarousel-skeleton');
    if (skeletonDiv) {
      skeletonDiv.remove();
    }
  }

  // Hide component when the booking is in less than X days before departure
  if (booking && isXDaysBeforeDeparture(getDepartureDate(booking))) {
    removeExploreByDaySkeleton();
    return null;
  }

  // Hide component when the login mode is 'light'
  if (loginMode !== LoginMode.FULL) {
    removeExploreByDaySkeleton();
    return null;
  }

  // Hide component when the booking is in optional status
  if (isOpt) {
    removeExploreByDaySkeleton();
    return null;
  }

  if (excursionData?.length === 0) {
    removeExploreByDaySkeleton();
    return null;
  }

  if (itineraryData && excursionData && excursionData.length > 0) {
    removeExploreByDaySkeleton();
  }

  // Return component when all itinerary data and excursion data are fetched
  return (
    itineraryData &&
    excursionData &&
    excursionData.length > 0 && (
      <div className={styles.exploreByDayCarouselWrapper}>
        <Text
          text={configurationProps.preTitle}
          styleOption={`text body-small spacer-mb-016 spacer-md-mb-024`}
          tagHtml="p"
        ></Text>
        <Text
          text={configurationProps.title}
          styleOption={`text header-m spacer-mb-016 spacer-md-mb-008`}
          tagHtml="p"
        ></Text>
        <Text
          text={configurationProps.subTitle}
          styleOption={`text body-default`}
          tagHtml="p"
        ></Text>
        <ExploreByDayCarousel
          configurationProps={configurationProps}
          excursionDataProps={excursionData}
          itineraryDataProps={itineraryData}
        />
      </div>
    )
  );
};

export default ExploreByDayCarouselWrapper;

ExploreByDayCarouselWrapper.aemResourceType =
  'b2c-website-foundation/components/mymsc/explorebydaycarousel';
